import React, { useState } from 'react';
import { Phone, Plus, Trash2, UserPlus, Mail, MessageSquare, CalendarClock, CalendarX } from 'lucide-react';

interface Delegato {
    nome: string;
    qualifica: string;
    luogo_nascita: string;
    data_nascita: string;
    telefono: string;
    email: string;
    ruolo?: string;
}

interface Props {
    formValues: any;
    setFormValues: (data: any) => void;
    delegati: Delegato[];
    setDelegati: (data: Delegato[]) => void;
    selectedModelId?: string;
}

const MAX_DELEGATI = 2;

export const Step3_ContattiDelegati: React.FC<Props> = ({ formValues, setFormValues, delegati, setDelegati, selectedModelId }) => {
    const [draft, setDraft] = useState<Delegato>({ nome: '', qualifica: '', luogo_nascita: '', data_nascita: '', telefono: '', email: '', ruolo: '' });

    const handleDraftChange = (field: keyof Delegato, value: string) => setDraft(prev => ({ ...prev, [field]: value }));

    const limiteRaggiunto = delegati.length >= MAX_DELEGATI;

    const addDelegato = () => {
        if (!draft.nome.trim()) return;
        if (limiteRaggiunto) return;
        setDelegati([...delegati, { ...draft, qualifica: draft.qualifica.trim() || 'Delegato' }]);
        setDraft({ nome: '', qualifica: '', luogo_nascita: '', data_nascita: '', telefono: '', email: '', ruolo: '' });
    };

    const removeDelegato = (index: number) => setDelegati(delegati.filter((_, i) => i !== index));

    const isConsulenzaOBonifica = ['prev-consulenza', 'prev-bonifiche'].includes(selectedModelId || '');

    const tempisticheBlock = (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-2">
                <CalendarClock size={24} className="text-blue-600"/> Tempistiche del Servizio
            </h2>
            <p className="text-sm text-gray-500 mb-6">Indica data, ora di inizio e termine previsto delle attività oggetto dell'incarico.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-2 flex items-center gap-1.5">
                        <CalendarClock size={13}/> Data e ora di inizio servizio
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Data inizio</label>
                            <input type="date"
                                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                   value={formValues.data_inizio_servizio || ''}
                                   onChange={e => setFormValues({ ...formValues, data_inizio_servizio: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Ora inizio</label>
                            <input type="time"
                                   className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                   value={formValues.orario_inizio_servizio || ''}
                                   onChange={e => setFormValues({ ...formValues, orario_inizio_servizio: e.target.value })} />
                        </div>
                    </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <label className="block text-xs font-bold text-gray-700 uppercase mb-2 flex items-center gap-1.5">
                        <CalendarX size={13}/> Termine del servizio
                    </label>
                    <input type="text"
                           placeholder="Es. 31/12/2026 oppure 'a conclusione delle attività'"
                           className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                           value={formValues.termine_servizio || ''}
                           onChange={e => setFormValues({ ...formValues, termine_servizio: e.target.value })} />
                    <p className="text-[11px] text-gray-500 mt-2 leading-snug">
                        Puoi indicare una data precisa oppure una formula descrittiva (es. "al raggiungimento dell'obiettivo").
                    </p>
                </div>
            </div>
        </div>
    );

    if (isConsulenzaOBonifica) {
        return (
            <div className="animate-in fade-in slide-in-from-right-8 space-y-6 max-w-4xl mx-auto">
                {tempisticheBlock}
            </div>
        );
    }

    return (
        <div className="animate-in fade-in slide-in-from-right-8 space-y-6 max-w-4xl mx-auto">
            {tempisticheBlock}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-2">
                    <Phone size={24} className="text-blue-600"/> Contatti & Recapiti
                </h2>
                <p className="text-sm text-gray-500 mb-6">Verifica ed eventualmente aggiorna i recapiti validi per questa specifica indagine.</p>

                {/* Recapiti principali */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 border-b border-gray-100 pb-8">
                    {/* Cellulare + Messaggistica */}
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Telefono Cellulare</label>
                        <input type="text" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                               value={formValues.telefono || ''} onChange={e => setFormValues({ ...formValues, telefono: e.target.value })} />

                        <div className="mt-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input type="checkbox" className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                                       checked={!!formValues.telefono_1}
                                       onChange={e => setFormValues({ ...formValues, telefono_1: e.target.checked, telefono_2: !e.target.checked })} />
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold text-gray-800 flex items-center gap-1.5 group-hover:text-blue-700 transition-colors">
                                        <MessageSquare size={14}/> Autorizzo uso messaggistica
                                    </span>
                                    <span className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                                        Autorizzo l'invio di comunicazioni e documenti tramite sistemi di messaggistica istantanea (es. WhatsApp, Telegram, SMS).
                                    </span>
                                </div>
                            </label>
                        </div>

                        <div className="mt-4">
                            <label className="block text-xs font-bold text-gray-500 mb-1">Secondo Cellulare (opzionale)</label>
                            <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                                   value={formValues.telefono_secondo || ''}
                                   onChange={e => setFormValues({ ...formValues, telefono_secondo: e.target.value })} />
                        </div>
                    </div>

                    {/* Email, PEC, Tel Fisso, Fax */}
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1 flex items-center gap-1">
                                <Mail size={11}/> Email
                            </label>
                            <input type="email" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                   value={formValues.email || ''} onChange={e => setFormValues({ ...formValues, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">PEC</label>
                            <input type="email" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                   value={formValues.pec || ''} onChange={e => setFormValues({ ...formValues, pec: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Tel. Fisso</label>
                                <input type="text" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                       value={formValues.telefono_fisso || ''} onChange={e => setFormValues({ ...formValues, telefono_fisso: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Fax</label>
                                <input type="text" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                       value={formValues.fax || ''} onChange={e => setFormValues({ ...formValues, fax: e.target.value })} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delegati */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                    <h3 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                        <UserPlus size={18} className="text-indigo-500"/> Soggetti Delegati
                        <span className="ml-auto text-[11px] font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                            {delegati.length}/{MAX_DELEGATI}
                        </span>
                    </h3>
                    <p className="text-xs text-gray-500 mb-5">
                        Persone autorizzate dal cliente a ricevere informazioni o ritirare il report. Massimo {MAX_DELEGATI} delegati.
                    </p>

                    {/* Form aggiunta delegato */}
                    <div className={`bg-white rounded-xl border border-indigo-100 p-4 mb-5 space-y-3 shadow-sm ${limiteRaggiunto ? 'opacity-60 pointer-events-none' : ''}`}>
                        <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Aggiungi Delegato</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Nome e Cognome *</label>
                                <input type="text" placeholder="Es. Mario Rossi"
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.nome}
                                       onChange={e => handleDraftChange('nome', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Qualifica / Ruolo</label>
                                <input type="text" placeholder="Es. Avvocato, Coniuge..."
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.qualifica}
                                       onChange={e => handleDraftChange('qualifica', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Luogo di Nascita</label>
                                <input type="text" placeholder="Es. Milano"
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.luogo_nascita}
                                       onChange={e => handleDraftChange('luogo_nascita', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Data di Nascita</label>
                                <input type="date"
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.data_nascita}
                                       onChange={e => handleDraftChange('data_nascita', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Telefono</label>
                                <input type="text" placeholder="Es. 333 1234567"
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.telefono}
                                       onChange={e => handleDraftChange('telefono', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                                <input type="email" placeholder="email@esempio.com"
                                       className="w-full p-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                                       value={draft.email}
                                       onChange={e => handleDraftChange('email', e.target.value)} />
                            </div>
                        </div>

                        <button onClick={addDelegato} disabled={!draft.nome.trim() || limiteRaggiunto}
                                className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2 mt-2">
                            <Plus size={16}/> Aggiungi Delegato
                        </button>
                    </div>

                    {limiteRaggiunto && (
                        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                            Hai raggiunto il numero massimo di {MAX_DELEGATI} delegati. Rimuovine uno per aggiungerne un altro.
                        </div>
                    )}

                    {/* Lista delegati aggiunti */}
                    {delegati.length > 0 ? (
                        <div className="space-y-2">
                            {delegati.map((d, i) => (
                                <div key={i} className="flex items-start justify-between bg-white p-4 rounded-lg border border-gray-200 shadow-sm gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-gray-800">{d.nome}</span>
                                            <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-md uppercase tracking-wider">
                                                {d.qualifica}
                                            </span>
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                                            {(d.luogo_nascita || d.data_nascita) && (
                                                <span className="text-xs text-gray-500">
                                                    Nato/a{d.luogo_nascita ? ` a ${d.luogo_nascita}` : ''}{d.data_nascita ? ` il ${d.data_nascita}` : ''}
                                                </span>
                                            )}
                                            {d.telefono && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10}/> {d.telefono}</span>}
                                            {d.email && <span className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10}/> {d.email}</span>}
                                        </div>
                                    </div>
                                    <button onClick={() => removeDelegato(i)} className="text-red-500 p-1.5 hover:bg-red-50 rounded-lg transition-colors shrink-0">
                                        <Trash2 size={15}/>
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5 text-xs text-gray-400 italic bg-white rounded-lg border border-dashed border-gray-300">
                            Nessun delegato aggiunto
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
